import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth, AuthProvider } from "./hooks/useAuth";
import Layout from "./components/Layout";
import TabBar from "./components/TabBar";
import HomePage from "./pages/HomePage";
import CreatePage from "./pages/CreatePage";
import FundPage from "./pages/FundPage";
import AcceptPage from "./pages/AcceptPage";
import ProfilePage from "./pages/ProfilePage";
import BetDetailPage from "./pages/BetDetailPage";
import VictoryCardPage from "./pages/VictoryCardPage";
import JoinBet from "./pages/JoinBet";
import api from "./api";
import { colors } from "./theme";

// Hide TabBar on full-screen flows (Create wizard + money screens)
const HIDE_TAB_ROUTES = ["/create", "/fund", "/accept", "/join", "/bet/"];

// VEN-39: routes that are publicly shareable — they must render even when
// there is no Telegram auth context (e.g. opened from a shared link in a
// browser). The pages behind these routes use the public bet API.
const PUBLIC_SHARE_ROUTE_RE = /^\/bet\/[^/]+\/(victory|defeat)$/;

/** Convert 32-char compact hex to standard UUID with dashes */
function compactToUuid(compact: string): string {
  if (compact.length === 32) {
    return `${compact.slice(0, 8)}-${compact.slice(8, 12)}-${compact.slice(12, 16)}-${compact.slice(16, 20)}-${compact.slice(20)}`;
  }
  return compact;
}

function decodeStartParamDescription(descB64: string): string {
  const normalized = descB64.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "==".slice(0, (4 - normalized.length % 4) % 4);
  return atob(padded);
}

function AppRoutes() {
  const location = useLocation();
  const navigate = useNavigate();
  const hideTab = HIDE_TAB_ROUTES.some((r) => location.pathname.startsWith(r));

  const deepLinkHandled = useRef(false);
  // Set when someone other than the bet creator taps a group card. The
  // Mini App renders a blocking screen instead of the wizard so they can't
  // accidentally deposit their own TON for someone else's bet.
  const [notOwnerInfo, setNotOwnerInfo] = useState<{
    creator: number;
    amountTon: number;
    description: string;
  } | null>(null);

  useEffect(() => {
    if (deepLinkHandled.current) return;

    const tg = window.Telegram?.WebApp;
    const startParam = tg?.initDataUnsafe?.start_param || "";
    const myTgId = (tg?.initDataUnsafe as { user?: { id?: number } } | undefined)?.user?.id;

    // --- New "owned" create link: only the original poster may fund ---
    if (startParam.startsWith("co_")) {
      const parts = startParam.split("_");
      if (parts.length >= 5) {
        deepLinkHandled.current = true;
        const creatorTgId = parseInt(parts[1], 10);
        const nanotons = parseInt(parts[2], 10);
        const chatId = parseInt(parts[3], 10);
        const description = decodeStartParamDescription(parts.slice(4).join("_"));

        // Fail-closed ownership gate (VEN-10): if the deep link carries a
        // creator id, only let the tap through when we can positively prove
        // the tapper is that creator. Any "I don't know who you are" case
        // (missing initDataUnsafe.user, NaN parse, timing race with the
        // Telegram SDK) must show the blocking screen, not fall through to
        // /fund.
        if (!Number.isNaN(creatorTgId) && creatorTgId > 0) {
          if (!myTgId || myTgId !== creatorTgId) {
            setNotOwnerInfo({
              creator: creatorTgId,
              amountTon: nanotons / 1_000_000_000,
              description,
            });
            return;
          }
        }

        navigate("/fund", {
          replace: true,
          state: {
            activity: "",
            target: "",
            deadline: "",
            amount: `${nanotons / 1_000_000_000}`,
            targetUser: null,
            description,
            category: "custom",
            expires_in_hours: 24,
            strava_bet_type: null,
            strava_target_value: null,
            bet_mode: "1v1",
            pile_on_type: null,
            telegram_group_id: Number.isNaN(chatId) ? undefined : chatId,
            creator_telegram_id: Number.isNaN(creatorTgId) ? undefined : creatorTgId,
          },
        });
      }
    } else if (startParam.startsWith("do_") || startParam.startsWith("vo_")) {
      // New "owned" dare/versus — payload format:
      //   {creator}:{nanotons}:{chatId}:{username}:{description}
      const prefix = startParam[0]; // "d" or "v"
      const payloadB64 = startParam.slice(3);
      const decoded = decodeStartParamDescription(payloadB64);

      const c1 = decoded.indexOf(":");
      const c2 = decoded.indexOf(":", c1 + 1);
      const c3 = decoded.indexOf(":", c2 + 1);
      const c4 = decoded.indexOf(":", c3 + 1);

      if (c4 > 0) {
        deepLinkHandled.current = true;
        const creatorTgId = parseInt(decoded.substring(0, c1), 10);
        const nanotons = parseInt(decoded.substring(c1 + 1, c2), 10);
        const chatId = parseInt(decoded.substring(c2 + 1, c3), 10);
        const targetUsername = decoded.substring(c3 + 1, c4);
        const description = decoded.substring(c4 + 1);
        const pileOnType = prefix === "d" ? "dare" : "versus";

        // Fail-closed ownership gate (VEN-10) — see `co_` branch above.
        if (!Number.isNaN(creatorTgId) && creatorTgId > 0) {
          if (!myTgId || myTgId !== creatorTgId) {
            setNotOwnerInfo({
              creator: creatorTgId,
              amountTon: nanotons / 1_000_000_000,
              description,
            });
            return;
          }
        }

        navigate("/fund", {
          replace: true,
          state: {
            activity: "",
            target: "",
            deadline: "",
            amount: `${nanotons / 1_000_000_000}`,
            targetUser: null,
            targetUsername,
            description,
            category: "custom",
            expires_in_hours: 24,
            strava_bet_type: null,
            strava_target_value: null,
            bet_mode: "pile_on",
            pile_on_type: pileOnType,
            telegram_group_id: Number.isNaN(chatId) ? undefined : chatId,
            creator_telegram_id: Number.isNaN(creatorTgId) ? undefined : creatorTgId,
          },
        });
      }
    } else if (startParam.startsWith("c_")) {
      // Legacy create link — kept for group messages posted before we
      // started stamping the creator telegram id into the deep link.
      const parts = startParam.split("_");
      if (parts.length >= 4) {
        deepLinkHandled.current = true;
        const nanotons = parseInt(parts[1], 10);
        const chatId = parseInt(parts[2], 10);
        const description = decodeStartParamDescription(parts.slice(3).join("_"));

        navigate("/fund", {
          replace: true,
          state: {
            activity: "",
            target: "",
            deadline: "",
            amount: `${nanotons / 1_000_000_000}`,
            targetUser: null,
            description,
            category: "custom",
            expires_in_hours: 24,
            strava_bet_type: null,
            strava_target_value: null,
            bet_mode: "1v1",
            telegram_group_id: Number.isNaN(chatId) ? undefined : chatId,
          },
        });
      }
    } else if (startParam.startsWith("d_") || startParam.startsWith("v_")) {
      const prefix = startParam[0]; // "d" or "v"
      const payloadB64 = startParam.slice(2);
      const decoded = decodeStartParamDescription(payloadB64);

      // payload format: nanotons:chatId:username:description
      const colonIndex1 = decoded.indexOf(":");
      const colonIndex2 = decoded.indexOf(":", colonIndex1 + 1);
      const colonIndex3 = decoded.indexOf(":", colonIndex2 + 1);

      if (colonIndex3 > 0) {
        deepLinkHandled.current = true;
        const nanotons = parseInt(decoded.substring(0, colonIndex1), 10);
        const chatId = parseInt(decoded.substring(colonIndex1 + 1, colonIndex2), 10);
        const targetUsername = decoded.substring(colonIndex2 + 1, colonIndex3);
        const description = decoded.substring(colonIndex3 + 1);
        const pileOnType = prefix === "d" ? "dare" : "versus";

        navigate("/fund", {
          replace: true,
          state: {
            activity: "",
            target: "",
            deadline: "",
            amount: `${nanotons / 1_000_000_000}`,
            targetUser: null,
            targetUsername,
            description,
            category: "custom",
            expires_in_hours: 24,
            strava_bet_type: null,
            strava_target_value: null,
            bet_mode: "pile_on",
            pile_on_type: pileOnType,
            telegram_group_id: Number.isNaN(chatId) ? undefined : chatId,
          },
        });
      }
    } else if (/^g-?\d+$/.test(startParam)) {
      // VEN-56 / VEN-57: handle_start_command stamps `g{chatId}` into the
      // startapp param when /start fires in a group, because Telegram
      // doesn't populate initDataUnsafe.chat for Mini Apps launched via a
      // regular `url` button. Persist the recovered group id so CreatePage
      // can attach it to the next POST /api/bets call.
      const groupId = parseInt(startParam.slice(1), 10);
      if (!Number.isNaN(groupId)) {
        try {
          sessionStorage.setItem("pending_group_id", String(groupId));
        } catch {
          // sessionStorage may be unavailable in some hardened WebView
          // contexts — failing here is fine; the bet just won't get the
          // group card, which matches today's behaviour.
        }
      }
      // Don't mark deepLinkHandled — we still want the default landing
      // (Home) to render. The group id is read from sessionStorage at
      // POST /api/bets time, so no navigation is required here.
    } else if (startParam === "profile") {
      // VEN-99: deep link to profile page (e.g. from HealthBridge connect notification)
      deepLinkHandled.current = true;
      navigate("/profile", { replace: true });
    } else if (startParam.startsWith("dare_")) {
      deepLinkHandled.current = true;
      const compact = startParam.slice(5);
      const uuid = compactToUuid(compact);
      navigate(`/join/${uuid}?role=target`, { replace: true });
    } else if (startParam.startsWith("vs_")) {
      deepLinkHandled.current = true;
      const compact = startParam.slice(3);
      const uuid = compactToUuid(compact);
      navigate(`/join/${uuid}?role=target`, { replace: true });
    } else if (startParam.startsWith("bet_")) {
      deepLinkHandled.current = true;

      // Check for side suffix: bet_{uuid}_for or bet_{uuid}_against
      let sideParam: string | null = null;
      let rawId = startParam.slice(4);
      if (rawId.endsWith("_for")) {
        sideParam = "for";
        rawId = rawId.slice(0, -4);
      } else if (rawId.endsWith("_against")) {
        sideParam = "against";
        rawId = rawId.slice(0, -8);
      }

      const uuid = compactToUuid(rawId);

      if (sideParam) {
        // pile_on join with pre-selected side
        navigate(`/join/${uuid}?side=${sideParam}`, { replace: true });
      } else {
        // Existing logic: fetch bet to determine route
        api.get(`/bets/${uuid}`).then((res) => {
          const bet = res.data;
          const myTgId = (tg?.initDataUnsafe as any)?.user?.id;

          if (bet.bet_mode === "pile_on" && bet.challenger_telegram_id !== myTgId) {
            // pile_on bet → Join page
            navigate(`/join/${uuid}`, { replace: true });
          } else if (bet.status?.toLowerCase() === "funded" && bet.challenger_telegram_id !== myTgId) {
            // 1v1 bet waiting for opponent → Accept page
            navigate(`/accept/${uuid}`, { replace: true });
          } else {
            // Creator or already live/resolved → BetDetail
            navigate(`/bet/${uuid}`, { replace: true });
          }
        }).catch(() => {
          navigate(`/accept/${uuid}`, { replace: true });
        });
      }
    }
  }, [navigate]);

  if (notOwnerInfo) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 32,
          textAlign: "center",
          gap: 12,
        }}
      >
        <div style={{ fontSize: 48 }}>🔒</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: colors.white }}>
          This bet isn't yours
        </div>
        <div style={{ fontSize: 14, color: colors.grayLight, lineHeight: 1.5, maxWidth: 320 }}>
          Only the person who posted this challenge can fund it. Ask them to open the
          message and tap <b>Create &amp; Fund</b> first — then you'll be able to accept
          or join from the bet card.
        </div>
        <div
          style={{
            marginTop: 8,
            padding: "12px 16px",
            borderRadius: 12,
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            fontSize: 13,
            color: colors.grayLight,
            maxWidth: 320,
          }}
        >
          📝 {notOwnerInfo.description}
          <br />
          💰 {notOwnerInfo.amountTon} TON
        </div>
        <button
          onClick={() => {
            setNotOwnerInfo(null);
            navigate("/", { replace: true });
          }}
          style={{
            marginTop: 16,
            padding: "12px 28px",
            borderRadius: 12,
            border: "none",
            background: colors.primary,
            color: colors.bg,
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Browse my bets
        </button>
      </div>
    );
  }

  return (
    <>
      {/* minHeight: 0 is required so a flex child with overflow actually
          scrolls instead of growing to fit its content. Without it, tall
          pages (e.g. Create Step 3) push past the viewport on small phones. */}
      <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/create" element={<CreatePage />} />
          <Route path="/fund" element={<FundPage />} />
          <Route path="/accept/:betId" element={<AcceptPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/join/:betId" element={<JoinBet />} />
          <Route path="/bet/:betId" element={<BetDetailPage />} />
          {/* VEN-39: separate path segment per outcome so Telegram link
              previews can't collapse winner/loser into one URL. */}
          <Route path="/bet/:betId/victory" element={<VictoryCardPage />} />
          <Route path="/bet/:betId/defeat" element={<VictoryCardPage />} />
        </Routes>
      </div>
      {!hideTab && <TabBar />}
    </>
  );
}

export default function App() {
  const { user, loading, error, isDev } = useAuth();
  const [showDebug, setShowDebug] = useState(false);

  // VEN-39: allow victory/defeat share pages to render without auth
  const isPublicShareRoute = PUBLIC_SHARE_ROUTE_RE.test(window.location.pathname);

  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setShowDebug(true), 5000);
    return () => clearTimeout(timer);
  }, [loading]);

  if (loading && !isPublicShareRoute) {
    const tg = window.Telegram?.WebApp;
    return (
      <Layout>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 16,
            color: colors.grayLight,
          }}
        >
          Loading...
          {showDebug && (
            <div style={{ marginTop: 16, fontSize: 11, color: "#666", fontFamily: "monospace", textAlign: "left", padding: "0 24px" }}>
              <div>initData: {tg?.initData ? "yes (" + tg.initData.length + " chars)" : "no"}</div>
              <div>start_param: {tg?.initDataUnsafe?.start_param || "none"}</div>
              <div>user: {tg?.initDataUnsafe?.user?.id || "none"}</div>
              <div>errors: {((window as any).__sbErrors || []).join(", ") || "none"}</div>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  if (error && !isPublicShareRoute) {
    return (
      <Layout>
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 16, color: colors.danger, marginBottom: 8 }}>
            {error}
          </div>
          <div style={{ fontSize: 14, color: colors.grayLight }}>
            Open this app inside Telegram
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {isDev && (
        <div
          style={{
            position: "fixed",
            top: 8,
            right: 8,
            background: colors.amber,
            color: "#000",
            fontSize: 10,
            fontWeight: 700,
            padding: "2px 6px",
            borderRadius: 4,
            zIndex: 9999,
            opacity: 0.85,
          }}
        >
          DEV MODE
        </div>
      )}
      <AuthProvider value={{ user }}>
        <AppRoutes />
      </AuthProvider>
    </Layout>
  );
}
